import { Fragment, useEffect, useState, useCallback } from 'react';
import {
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Loader2,
  RefreshCw,
  UserRoundCheck,
  XCircle,
} from 'lucide-react';
import apiClient from '../../lib/api';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { Textarea } from '@easyflow/ui-frontend/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@easyflow/ui-frontend/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@easyflow/ui-frontend/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';

interface Candidate {
  user_id: string;
  name: string;
  score: number;
  eligible: boolean;
  exclusion_reason: string | null;
  attendance_state: string | null;
}

interface Plan {
  id: string;
  appointment_id: string;
  appointment_title: string | null;
  start_time: string | null;
  end_time: string | null;
  original_first_name: string | null;
  original_last_name: string | null;
  proposed_first_name: string | null;
  proposed_last_name: string | null;
  proposed_user_id: string | null;
  reason: string | null;
  status: string;
  candidates: Candidate[];
  availability_mode: string | null;
  notes: string | null;
  source_reasons?: string[];
  created_at: string;
}

const STATUS_LABEL: Record<string, string> = {
  PROPOSED: 'Proposto',
  PROPOSED_NO_MATCH: 'Nessun match',
  READY_TO_APPLY: 'Pronto',
  APPLIED: 'Applicato',
  EXPIRED: 'Scaduto',
  CANCELLED: 'Annullato',
};

const STATUS_VARIANT: Record<string, string> = {
  PROPOSED: 'bg-amber-100 text-amber-800 border-amber-200',
  PROPOSED_NO_MATCH: 'bg-rose-100 text-rose-800 border-rose-200',
  READY_TO_APPLY: 'bg-blue-100 text-blue-800 border-blue-200',
  APPLIED: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  EXPIRED: 'bg-slate-100 text-slate-700 border-slate-200',
  CANCELLED: 'bg-slate-100 text-slate-700 border-slate-200',
};

function planReason(plan: Plan) {
  if (plan.source_reasons?.includes('HR_APPROVED_ABSENCE') || plan.reason === 'HR_APPROVED_ABSENCE') {
    return 'Assenza HR approvata';
  }
  if (plan.reason === 'LATE_TO_ABSENT') return 'Assenza rilevata';
  if (plan.reason === 'EARLY_CLOCKOUT') return 'Uscita anticipata';
  return plan.reason || '—';
}

function candidateName(plan: Plan) {
  return `${plan.proposed_first_name || ''} ${plan.proposed_last_name || ''}`.trim() || null;
}

export default function ReassignmentPlansPanel() {
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [filter, setFilter] = useState<string>('OPEN');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [candidatesByPlan, setCandidatesByPlan] = useState<Record<string, Candidate[]>>({});
  const [candidatesLoadingId, setCandidatesLoadingId] = useState<string | null>(null);
  const [selectedCandidateByPlan, setSelectedCandidateByPlan] = useState<Record<string, string>>({});
  const [decisionNoteByPlan, setDecisionNoteByPlan] = useState<Record<string, string>>({});
  const [actionId, setActionId] = useState<string | null>(null);
  const [confirmApply, setConfirmApply] = useState<Plan | null>(null);
  const [cancelPlan, setCancelPlan] = useState<Plan | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const { toast } = useToast();

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const r = await apiClient.get<Plan[]>(`/calendar/reassignment-plans${filter ? `?status=${filter}` : ''}`);
      if (r.success && r.data) {
        setPlans(r.data);
      } else {
        setLoadError(r.error || 'Impossibile caricare i piani di riassegnazione');
      }
    } catch (error) {
      setLoadError(error instanceof Error ? error.message : 'Impossibile caricare i piani di riassegnazione');
    } finally {
      setLoading(false);
    }
  }, [filter]);
  useEffect(() => { load(); }, [filter, load]);

  const toggleDetails = async (plan: Plan) => {
    if (expandedId === plan.id) {
      setExpandedId(null);
      return;
    }
    setExpandedId(plan.id);
    setCandidatesLoadingId(plan.id);
    try {
      const response = await apiClient.get<Candidate[]>(`/calendar/reassignment-plans/${plan.id}/candidates`);
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Impossibile aggiornare i candidati');
      }
      setCandidatesByPlan(current => ({ ...current, [plan.id]: response.data! }));
      const currentProposed = response.data.find(candidate => candidate.user_id === plan.proposed_user_id);
      if (currentProposed?.eligible) {
        setSelectedCandidateByPlan(current => ({ ...current, [plan.id]: currentProposed.user_id }));
      }
    } catch (error) {
      toast({
        title: 'Candidati non disponibili',
        description: error instanceof Error ? error.message : 'Impossibile caricare i candidati',
        variant: 'destructive',
      });
    } finally {
      setCandidatesLoadingId(null);
    }
  };

  const assign = async (plan: Plan) => {
    const proposedUserId = selectedCandidateByPlan[plan.id];
    const note = decisionNoteByPlan[plan.id]?.trim();
    if (!proposedUserId || !note || note.length < 3) {
      toast({
        title: 'Completa la decisione',
        description: 'Seleziona un sostituto disponibile e inserisci una motivazione di almeno 3 caratteri.',
        variant: 'destructive',
      });
      return;
    }
    setActionId(plan.id);
    try {
      const response = await apiClient.post(`/calendar/reassignment-plans/${plan.id}/assign`, {
        proposed_user_id: proposedUserId,
        note,
      });
      if (!response.success) throw new Error(response.error || 'Impossibile salvare il sostituto');
      toast({ title: 'Sostituto selezionato', description: 'Il piano è pronto per la conferma finale.' });
      setExpandedId(null);
      await load();
    } catch (error) {
      toast({
        title: 'Assegnazione non salvata',
        description: error instanceof Error ? error.message : 'Impossibile salvare il sostituto',
        variant: 'destructive',
      });
    } finally {
      setActionId(null);
    }
  };

  const apply = async () => {
    if (!confirmApply) return;
    setActionId(confirmApply.id);
    try {
      const response = await apiClient.post(`/calendar/reassignment-plans/${confirmApply.id}/apply`);
      if (!response.success) throw new Error(response.error || 'Impossibile applicare la riassegnazione');
      toast({ title: 'Riassegnazione applicata', description: 'L’appuntamento è stato aggiornato.' });
      setConfirmApply(null);
      await load();
    } catch (error) {
      toast({
        title: 'Riassegnazione non applicata',
        description: error instanceof Error ? error.message : 'Impossibile applicare la riassegnazione',
        variant: 'destructive',
      });
    } finally {
      setActionId(null);
    }
  };

  const cancel = async () => {
    if (!cancelPlan || cancelReason.trim().length < 3) return;
    setActionId(cancelPlan.id);
    try {
      const response = await apiClient.post(`/calendar/reassignment-plans/${cancelPlan.id}/cancel`, {
        reason: cancelReason.trim(),
      });
      if (!response.success) throw new Error(response.error || 'Impossibile annullare il piano');
      toast({ title: 'Piano annullato', description: 'L’appuntamento non è stato modificato.' });
      setCancelPlan(null);
      setCancelReason('');
      await load();
    } catch (error) {
      toast({
        title: 'Annullamento non riuscito',
        description: error instanceof Error ? error.message : 'Impossibile annullare il piano',
        variant: 'destructive',
      });
    } finally {
      setActionId(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="min-w-0 flex-1">
          <h3 className="text-base font-semibold text-foreground">Piani da gestire</h3>
          <p className="text-sm text-muted-foreground">
            Scegli e conferma manualmente un sostituto: nessun appuntamento viene modificato in automatico.
          </p>
        </div>
        <Select value={filter || 'ALL'} onValueChange={(v) => setFilter(v === 'ALL' ? '' : v)}>
          <SelectTrigger className="w-full sm:w-[190px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="OPEN">Da gestire</SelectItem>
            <SelectItem value="ALL">Tutte</SelectItem>
            <SelectItem value="PROPOSED">Proposte</SelectItem>
            <SelectItem value="PROPOSED_NO_MATCH">Senza match</SelectItem>
            <SelectItem value="READY_TO_APPLY">Pronte da applicare</SelectItem>
            <SelectItem value="APPLIED">Applicate</SelectItem>
            <SelectItem value="EXPIRED">Scadute</SelectItem>
            <SelectItem value="CANCELLED">Annullate</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={load} disabled={loading}>
          {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
          Aggiorna
        </Button>
      </div>

      {loadError && (
        <div className="flex items-start gap-3 rounded-lg border border-destructive/40 bg-destructive/5 p-4 text-sm">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
          <div className="min-w-0 flex-1">
            <p className="font-medium text-destructive">Impossibile caricare i piani</p>
            <p className="mt-1 text-muted-foreground">{loadError}</p>
          </div>
          <Button size="sm" variant="outline" onClick={load}>Riprova</Button>
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="min-w-[860px] w-full divide-y divide-border text-sm">
          <thead className="bg-muted/50">
            <tr>
              <th className="p-3 text-left font-medium text-foreground">Appuntamento</th>
              <th className="p-3 text-left font-medium text-foreground">Assenza</th>
              <th className="p-3 text-left font-medium text-foreground">Sostituto</th>
              <th className="p-3 text-left font-medium text-foreground">Stato</th>
              <th className="p-3 text-right font-medium text-foreground">Azioni</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border bg-card">
            {plans.map(plan => {
              const canManage = ['PROPOSED', 'PROPOSED_NO_MATCH', 'READY_TO_APPLY'].includes(plan.status);
              const isExpanded = expandedId === plan.id;
              const selectedCandidate = selectedCandidateByPlan[plan.id];
              const candidates = candidatesByPlan[plan.id] ?? [];
              return (
                <Fragment key={plan.id}>
                  <tr key={plan.id} className={isExpanded ? 'bg-muted/20' : undefined}>
                    <td className="p-3">
                      <div className="font-medium text-foreground">{plan.appointment_title || 'Appuntamento senza titolo'}</div>
                      <div className="mt-1 text-xs text-muted-foreground">
                        {plan.start_time ? new Date(plan.start_time).toLocaleString('it-IT') : 'Data non disponibile'}
                      </div>
                    </td>
                    <td className="p-3">
                      <div className="font-medium text-foreground">{planReason(plan)}</div>
                      <div className="mt-1 text-xs text-muted-foreground">
                        {`${plan.original_first_name || ''} ${plan.original_last_name || ''}`.trim() || 'Risorsa non disponibile'}
                      </div>
                    </td>
                    <td className="p-3 text-foreground">
                      {candidateName(plan) || <span className="text-muted-foreground">Da scegliere</span>}
                    </td>
                    <td className="p-3">
                      <Badge variant="outline" className={STATUS_VARIANT[plan.status]}>
                        {STATUS_LABEL[plan.status] || plan.status}
                      </Badge>
                    </td>
                    <td className="p-3 text-right">
                      {canManage && (
                        <div className="flex justify-end gap-2">
                          <Button size="sm" variant="outline" onClick={() => toggleDetails(plan)}>
                            {isExpanded ? <ChevronUp className="mr-1 h-4 w-4" /> : <ChevronDown className="mr-1 h-4 w-4" />}
                            Gestisci
                          </Button>
                          {plan.status === 'READY_TO_APPLY' && (
                            <Button size="sm" onClick={() => setConfirmApply(plan)} disabled={actionId === plan.id}>
                              <CheckCircle2 className="mr-1 h-4 w-4" />
                              Applica
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-destructive hover:text-destructive"
                            onClick={() => { setCancelPlan(plan); setCancelReason(''); }}
                            disabled={actionId === plan.id}
                          >
                            <XCircle className="mr-1 h-4 w-4" />
                            Annulla
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr key={`${plan.id}-details`} className="bg-muted/20">
                      <td colSpan={5} className="p-4">
                        <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_320px]">
                          <div className="space-y-3">
                            <div className="flex items-center gap-2">
                              <UserRoundCheck className="h-4 w-4 text-primary" />
                              <h4 className="font-medium text-foreground">Candidati aggiornati</h4>
                              {candidatesLoadingId === plan.id && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
                            </div>
                            {candidates.length === 0 && candidatesLoadingId !== plan.id ? (
                              <p className="rounded-md border border-dashed p-3 text-sm text-muted-foreground">
                                Nessun sostituto risulta disponibile in questo momento.
                              </p>
                            ) : (
                              <div className="space-y-2">
                                {candidates.map(candidate => (
                                  <label
                                    key={candidate.user_id}
                                    className={`flex cursor-pointer items-start gap-3 rounded-md border p-3 transition-colors ${
                                      candidate.eligible
                                        ? selectedCandidate === candidate.user_id ? 'border-primary bg-primary/5' : 'hover:border-primary/50'
                                        : 'cursor-not-allowed border-border bg-muted/40 opacity-70'
                                    }`}
                                  >
                                    <Input
                                      type="radio"
                                      name={`candidate-${plan.id}`}
                                      value={candidate.user_id}
                                      checked={selectedCandidate === candidate.user_id}
                                      disabled={!candidate.eligible || actionId === plan.id}
                                      onChange={() => setSelectedCandidateByPlan(current => ({ ...current, [plan.id]: candidate.user_id }))}
                                      className="mt-0.5 h-4 w-4"
                                    />
                                    <span className="min-w-0 flex-1">
                                      <span className="flex flex-wrap items-center gap-2">
                                        <span className="font-medium text-foreground">{candidate.name}</span>
                                        <Badge variant="outline" className={candidate.eligible ? 'border-success/40 bg-success/10 text-success' : 'border-border text-muted-foreground'}>
                                          {candidate.eligible ? 'Disponibile' : 'Non disponibile'}
                                        </Badge>
                                        <span className="text-xs text-muted-foreground">Punteggio {candidate.score}</span>
                                      </span>
                                      <span className="mt-1 block text-xs text-muted-foreground">
                                        {candidate.eligible
                                          ? `Stato presenza: ${candidate.attendance_state || 'non rilevato'}`
                                          : candidate.exclusion_reason || 'Non idoneo per questo appuntamento'}
                                      </span>
                                    </span>
                                  </label>
                                ))}
                              </div>
                            )}
                          </div>
                          <div className="space-y-3 rounded-lg border bg-background p-4">
                            <div>
                              <Label htmlFor={`decision-note-${plan.id}`}>Motivazione della scelta</Label>
                              <p className="mt-1 text-xs text-muted-foreground">
                                Verrà registrata sul piano prima dell’applicazione.
                              </p>
                            </div>
                            <Textarea
                              id={`decision-note-${plan.id}`}
                              value={decisionNoteByPlan[plan.id] || ''}
                              onChange={event => setDecisionNoteByPlan(current => ({ ...current, [plan.id]: event.target.value }))}
                              placeholder="Es. copertura confermata con il responsabile di sede"
                              disabled={actionId === plan.id}
                              className="min-h-[96px]"
                            />
                            <Button className="w-full" onClick={() => assign(plan)} disabled={actionId === plan.id || candidatesLoadingId === plan.id}>
                              {actionId === plan.id ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <UserRoundCheck className="mr-2 h-4 w-4" />}
                              Salva sostituto
                            </Button>
                            {plan.notes && <p className="border-t pt-3 text-xs leading-relaxed text-muted-foreground">{plan.notes}</p>}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
            {plans.length === 0 && !loading && (
              <tr><td colSpan={5} className="p-8 text-center text-muted-foreground">Nessun piano corrisponde al filtro.</td></tr>
            )}
            {loading && (
              <tr><td colSpan={5} className="p-8 text-center text-muted-foreground"><Loader2 className="mr-2 inline h-4 w-4 animate-spin" />Caricamento piani…</td></tr>
            )}
          </tbody>
        </table>
      </div>

      <AlertDialog open={Boolean(confirmApply)} onOpenChange={open => !open && setConfirmApply(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Applicare la riassegnazione?</AlertDialogTitle>
            <AlertDialogDescription>
              L’appuntamento verrà assegnato a {confirmApply ? candidateName(confirmApply) || 'il sostituto selezionato' : 'questo sostituto'}.
              Questa è l’unica azione che modifica l’appuntamento.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={actionId === confirmApply?.id}>Indietro</AlertDialogCancel>
            <AlertDialogAction onClick={apply} disabled={actionId === confirmApply?.id}>
              {actionId === confirmApply?.id && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Conferma e applica
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={Boolean(cancelPlan)} onOpenChange={open => {
        if (!open) {
          setCancelPlan(null);
          setCancelReason('');
        }
      }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Annullare il piano?</AlertDialogTitle>
            <AlertDialogDescription>
              L’appuntamento resterà invariato. Indica la motivazione da registrare nel piano.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="space-y-2">
            <Label htmlFor="reassignment-cancel-reason">Motivazione</Label>
            <Textarea
              id="reassignment-cancel-reason"
              value={cancelReason}
              onChange={event => setCancelReason(event.target.value)}
              placeholder="Es. appuntamento ripianificato direttamente con il cliente"
              disabled={actionId === cancelPlan?.id}
            />
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={actionId === cancelPlan?.id}>Indietro</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={event => {
                if (cancelReason.trim().length < 3) {
                  event.preventDefault();
                  toast({
                    title: 'Motivazione obbligatoria',
                    description: 'Inserisci almeno 3 caratteri per annullare il piano.',
                    variant: 'destructive',
                  });
                  return;
                }
                void cancel();
              }}
              disabled={actionId === cancelPlan?.id}
            >
              {actionId === cancelPlan?.id && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Conferma annullamento
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
